import { loadDraftMap, loadMap as loadPublicMap } from "@/api/firebaseAnon";
import { TFeature, TLineString, TPoint } from '@/shared/types/geojson';
import { DraftLoadData, MapData } from "@/shared/types/types";
import { ComputedMarker, ComputedShape, Route, Shape, ShapeLayer } from '@/shared/types/zod';
import { loc } from "@/data/loc";
import { mapStyles } from "@/data/MapStyles";
import { getAutoMarkers, KM_TO_MILES } from "@/Util";
import { getTextColor } from "@/utils/Color";
import { getElevationGain } from "@/utils/Elevation";
import { getLineIndex } from "@/utils/lineSampling";
import { getMarkerProgresses } from "@/utils/markerProgress";
import { Map as MaplibreMap } from "maplibre-gl";
import { defineStore } from "pinia";
import { computed, ref, shallowRef } from "vue";
import { generateShape } from "@/shared/utils/shapes";
import { collectUsedZoomStops, enrichFeatureWithSizeProps } from "@/shared/utils/iconSize";
import { EventBus } from "@/utils/EventBus";


export enum MapState {
	None,
	Loading,
	Loaded,
	NotFound,
	Error,
	Saving,
}

/** Target length (km) for synthetic legs created on routes that have none. */
const SYNTHETIC_LEG_KM = 5;
const KM_PER_DEG = 111;

/**
 * Cheap equirectangular distance (km) between two `[lng, lat]` points. `cosLat`
 * is precomputed by the caller so we avoid a trig call per segment. Exact
 * precision is irrelevant here — it's only used to space synthetic legs.
 */
function cheapSegmentKm(a: number[], b: number[], cosLat: number): number {
	const dLng = (b[0] - a[0]) * cosLat;
	const dLat = b[1] - a[1];
	return Math.sqrt(dLng * dLng + dLat * dLat) * KM_PER_DEG;
}

/**
 * Builds evenly spaced synthetic legs (~{@link SYNTHETIC_LEG_KM} km each) for a
 * route that has no user-defined legs. Returns `{ index, color }` boundaries
 * compatible with the real leg slicing logic, all sharing the route color.
 *
 * Uses a cheap equirectangular distance estimate (no trig per segment) — exact
 * length is irrelevant here, we just want legs roughly in the 10 km range so
 * the line-gradient stays short.
 */
function buildSyntheticLegs(coordinates: number[][][], color: string): { index: number; color: string }[] {
	if (coordinates.length < 2) return [];
	const cosLat = Math.cos(coordinates[0][0][1] * Math.PI / 180);
	const legs = [{ index: 0, color }];
	let accumulated = 0;
	for (let i = 1; i < coordinates.length; i++) {
		const a = coordinates[i - 1][0];
		const b = coordinates[i][0];
		accumulated += cheapSegmentKm(a, b, cosLat);
		if (accumulated >= SYNTHETIC_LEG_KM) {
			legs.push({ index: i, color });
			accumulated = 0;
		}
	}
	return legs;
}

export const useStore = defineStore("store", () => {

	const map = ref<MapData>()
	const mapState = ref<MapState>(MapState.None)
	const loadedSaveId = ref("")
	const latestSaveId = ref("")
	const selectedRouteId = ref("")
	const requestedLanguage = ref("sv")
	const satellite = ref(false)
	const videoMode = ref(false)
	const blockUi = ref(false) // used when drawing route

	const mapInstance = shallowRef<MaplibreMap | undefined>()

	const _editMode = ref(false)

	const isStale = computed(() => {
		return mapState.value != MapState.Saving
			&& latestSaveId.value !== ""
			&& loadedSaveId.value !== latestSaveId.value;
	})


	const currentRoute = computed(() => {
		if (map.value) {
			const r = map.value.routes.find((r) => r.id == selectedRouteId.value);
			if (r) {
				return r;
			}
		}
		return undefined;
	});

	const dropRoutes = computed(() => {
		// Edge case for viewing a route (via direct slug link) that
		// is not supposed to be in the list. Just show the name
		// of the single route...
		if (currentRoute.value?.properties.hideRoute) {
			return [{
				id: currentRoute.value.id as string,
				label: localized(currentRoute.value, "label")
			}]
		}

		return routes.value
			.filter(r => r.properties.hideRoute !== true)
			.map(r => ({
				id: r.id as string,
				label: localized(r, "label")
			}));
	})

	const drawFeature = computed(() => {
		//TODO: pick a selected route
		const route = currentRoute.value;
		let coords = route?.geometry?.coordinates;
		if (!coords || coords.length == 0) coords = [[[0, 0]], [[0, 0]]];
		return {
			type: "Feature",
			id: route?.id,
			geometry: {
				type: "LineString",
				coordinates: coords.map((c) => c[0]),
			},
			properties: { ...route?.properties },
		} satisfies TLineString;
	})

	const trackFeature = computed(() => {
		const route = currentRoute.value;
		let coords = route?.geometry?.coordinates;
		if (!coords || coords.length == 0) coords = [[[0, 0]], [[0, 0]]];
		const feature: TLineString = {
			type: "Feature",
			id: route?.id /*+ "_track"*/,
			geometry: {
				type: "LineString",
				coordinates: coords.flat(),
			},
			properties: { ...route?.properties },
		};
		// length is intentionally not set here; consumers read it from trackIndex
		// (getLineIndex), which is the same cached LineIndex over this geometry.
		feature.properties.__trackFeature = true;
		return feature;
	})

	const trackFeatureLegs = computed(() => {
		const route = currentRoute.value;
		if (!route) return [];
		if (!route.properties.legs || !route.properties.legs.length) return [trackFeature.value];
		return buildLegFeatures(route, route.properties.legs);
	})

	// Like trackFeatureLegs, but routes without user-defined legs are split into
	// synthetic ~5 km legs. Each leg becomes its own animation layer, which keeps
	// every line-gradient short enough that MapLibre's `line-progress` stays
	// accurately aligned with the track tip even on very long routes. Use this for
	// rendering only; trackFeatureLegs stays semantic for pace calculation/editing.
	const trackFeatureRenderLegs = computed(() => {
		const route = currentRoute.value;
		if (!route) return [];
		const legs = (route.properties.legs && route.properties.legs.length)
			? route.properties.legs
			: buildSyntheticLegs(route.geometry.coordinates, route.properties.color);
		if (!legs.length) return [trackFeature.value];
		return buildLegFeatures(route, legs);
	})

	function buildLegFeatures(route: Route, legs: { index: number; color: string }[]): TLineString[] {
		// Leg boundaries are *segment* indices into the MultiLineString, but the
		// shared cumulative-distance table (trackIndex) is keyed by *flattened*
		// vertex index. When a segment contains many points (road following) the
		// two index spaces differ, so build a prefix sum mapping each segment to
		// its first vertex in the flattened track before reading distances.
		const index = trackIndex.value;
		const allCoords = route.geometry.coordinates;
		const segStartVertex: number[] = new Array(allCoords.length + 1);
		let vtx = 0;
		for (let s = 0; s < allCoords.length; s++) {
			segStartVertex[s] = vtx;
			vtx += allCoords[s].length;
		}
		segStartVertex[allCoords.length] = vtx; // total flattened vertex count
		const lastVertexOfRoute = vtx - 1;
		let features: TLineString[] = [];
		for (let i = 0; i < legs.length; i++) {
			const leg = legs[i];
			const start = leg.index;
			const end = i < legs.length - 1
				? Math.min(legs[i + 1].index, allCoords.length - 1)
				: allCoords.length;
			const coords = allCoords.slice(start, end);
			if (i < legs.length - 1)
				coords.push([allCoords[end][0]]);
			const feature: TLineString = {
				type: "Feature",
				id: route.id + "_track_" + i,
				geometry: {
					type: "LineString",
					coordinates: coords.flat(),
				},
				properties: {
					...route.properties,
					color: leg.color,
				},
			}
			const startVertex = segStartVertex[start];
			const endVertex = i < legs.length - 1 ? segStartVertex[end] : lastVertexOfRoute;
			feature.properties!.length = index.distanceAtVertex(endVertex) - index.distanceAtVertex(startVertex);
			features.push(feature);
		}
		let startProgress = 0;
		const totalLength = index.length;
		for (let i = 0; i < features.length; i++) {
			const feature = features[i];
			const legLength = feature.properties!.length;
			const endProgress = startProgress + legLength / totalLength;
			feature.properties!.startProgress = startProgress;
			feature.properties!.endProgress = endProgress;
			startProgress = endProgress;
		}
		return features;
	}

	const heightData = computed(() => {
		return currentRoute.value && !currentRoute.value.properties.hideElevation
			? currentRoute.value.properties.heights
			: [];
	})

	const elevationGain = computed(() => {
		const gain = currentRoute.value?.properties?.gain ?? NaN / (map.value?.unit == 'mi' ? 3.2808399 : 1)
		const loss = currentRoute.value?.properties?.loss ?? NaN / (map.value?.unit == 'mi' ? 3.2808399 : 1)
		if (Number.isFinite(gain) && Number.isFinite(loss)) {
			return { gain, loss }
		}
		const values = getElevationGain(heightData.value)
		if (Number.isFinite(gain)) values.gain = gain
		if (Number.isFinite(loss)) values.loss = loss
		return values
	})

	const maxAltitude = computed((): number | undefined => {
		const override = currentRoute.value?.properties?.maxAltitude ?? NaN
		if (Number.isFinite(override)) return override

		const heights = currentRoute.value?.properties?.heights
		if (!heights || heights.length === 0) return undefined

		let max = -Infinity
		for (const height of heights) {
			if (!Number.isFinite(height)) continue
			if (height > max) max = height
		}

		return Number.isFinite(max) ? max : undefined
	})

	const hasElevation = computed((): boolean => {
		return !currentRoute.value?.properties?.hideElevation && heightData.value && heightData.value.length != 0
	})


	const trackLength = computed((): number => {
		const track = trackFeature.value;

		const trackLength = Number.parseFloat(track?.properties?.trackLength);
		if (Number.isFinite(trackLength)) {
			if (map.value?.unit == 'mi') {
				return trackLength * KM_TO_MILES
			}
			return trackLength;
		}

		return actualTrackLength.value;
	})

	// Canonical sampling/nearest index for the current track. Rebuilt only when
	// the track geometry changes; every track-related call site should read this
	// instead of calling getLineIndex itself, so they all share one structure.
	const trackIndex = computed(() => getLineIndex(trackFeature.value.geometry));

	const actualTrackLength = computed(() => {
		return trackIndex.value.length;
	})

	const heightColorOverride = computed(() => {
		const r = currentRoute.value;
		if (r) {
			if (r.properties.customHeightColor)
				return r.properties.heightColor;
			return undefined;
		}
		return undefined;
	});

	const markerCategories = computed(() => {
		return map.value ? map.value.markers : [];
	})

	const shapes = computed(() => {
		return map.value &&
			map.value.shapeLayer &&
			Array.isArray(map.value.shapeLayer.shapes)
			? map.value.shapeLayer.shapes
			: [];
	});

	const flatShapes = computed(() => {
		return shapes.value.reduce((res, item) => {
			if ("shapes" in item) res = [...res, ...item.shapes];
			else res.push(item);
			return res;
		}, [] as Shape[]);
	})

	const computedShapes = computed(() => {
		const shapes = flatShapes.value.map(feature => {
			const excluded = Array.isArray(feature.properties!.exclude) && feature.properties!.exclude.includes(selectedRouteId.value);
			return generateShape(feature, excluded);
		})

		// Enrich image shapes with __sz_N properties for icon-size expressions
		const zoomStops = collectUsedZoomStops(shapes);
		return shapes.map(feature => enrichFeatureWithSizeProps(feature, zoomStops));
	});

	const usedZoomStops = computed(() => {
		return collectUsedZoomStops(computedShapes.value);
	});

	const flatMarkers = computed(() => {
		if (map.value) {
			return map.value.markers.reduce((res, item) => {
				if (item.markers) res = [...res, ...item.markers];
				return res;
			}, [] as TPoint[]);
		}
		return [];
	})

	const categoryFilter = ref<string[]>([])

	function toggleCategoryFilter(catId: string) {
		const idx = categoryFilter.value.indexOf(catId);
		if (idx >= 0) {
			categoryFilter.value = categoryFilter.value.filter(id => id !== catId);
			return false
		} else {
			categoryFilter.value = [...categoryFilter.value, catId];
			return true
		}
	}

	function clearCategoryFilter() {
		categoryFilter.value = [];
	}

	const autoMarkers = computed(() => {
		return getAutoMarkers(trackFeature.value, trackLength.value, languageSuffixes.value);
	});

	const computedCategories = computed(() => {
		return [
			{
				name: "Auto",
				markers: [...autoMarkers.value]
			},
			...markerCategories.value
		]
	})

	const computedMarkersAll = computed(() => {
		if (map.value) {
			//TODO PINIA: category should be MarkerCategory?
			return computedCategories.value.reduce((res: ComputedMarker[], category: /*MarkerCategory*/any) => {
				if (category.markers)
					res = [...res, ...category.markers
						.filter(marker => _editMode.value || !(Array.isArray(marker.properties!.exclude) && marker.properties!.exclude.includes(selectedRouteId.value)))
						.map(marker => {
							const f = {
								type: "Feature",
								id: marker.id,
								properties: {
									...marker.properties,
									id: marker.id, // clicking on a label uses this id in MapSymbols.vue..
									__categoryId: category.id,
									__excluded: Array.isArray(marker.properties!.exclude) && marker.properties!.exclude.includes(selectedRouteId.value),
								},
								geometry: { ...marker.geometry },
							} as ComputedMarker;

							f.properties.__progress = getMarkerProgresses(
								trackIndex.value,
								marker.geometry.coordinates as [number, number],
								actualTrackLength.value,
							);


							//only override if empty.
							if (!f.properties!.color)
								f.properties!.color = category.color;
							if (!f.properties!.icon)
								f.properties!.icon = category.icon;

							f.properties!.textColor = getTextColor(f.properties!.color);

							// Copy global marker variation to each computed marker for reactive cache invalidation
							f.properties!.__markerVariation = map.value?.markerVariation ?? 'default';

							// if current language (and fallback language) is empty, override
							// with category label
							for (let i = languageSuffixes.value.length - 1; i >= 0; i--) {
								const suffix = languageSuffixes.value[i];
								const propName = `label${suffix}`;
								if (!f.properties![propName] && !f.properties.label) {
									f.properties![propName] = category[`label${suffix}`];
								}
							}
							return f;
						})]
				return res;
			}, []);
		}
		return [];
	})

	const computedMarkers = computed(() => {
		return computedMarkersAll.value.filter(marker =>
			categoryFilter.value.length === 0
			|| !marker.properties.__categoryId
			|| categoryFilter.value.includes(marker.properties.__categoryId)
		);
	})

	const computedMarkersNotGenerated = computed(() => {
		return computedMarkers.value.filter(m => !m.properties.__generated);
	})

	const routes = computed(() => {
		return map.value ? map.value.routes : [];
	})

	function isRoute(feature: TFeature | undefined) {
		return (
			feature != undefined && routes.value.find((r) => r.id == feature.id) !== undefined
		);
	}

	function isShape(feature: TFeature | undefined) {
		return (
			feature != undefined && flatShapes.value.find((r) => r.id == feature.id && "properties" in feature) !== undefined
		);
	}

	const allFeatures = computed(() => {
		return [...flatMarkers.value, ...routes.value, ...flatShapes.value];
	})

	const featuresBySlug = computed(() => {
		// features with the same slug will overwrite each other here, and the fact that shapes
		// comes after routes in the allFeatures getter above makes sure that a shape can be used to define
		// the bounding box of a route...
		return allFeatures.value.reduce(
			(list: Map<string, TFeature>, feature: TFeature) => {
				if (feature.properties!.slug && feature.properties!.slug != "") {
					list.set(feature.properties!.slug, feature);
				}
				return list;
			},
			new Map<string, TFeature>()
		);
	})

	const basemap = computed(() => {
		if (satellite.value && map.value?.satellite) {
			const sat = mapStyles.find(s => s.value == "alidade_satellite");
			if (sat) return sat
		}

		return mapStyles.find(s => s.value == map.value?.basemap) ?? mapStyles[0];
	})

	const isDarkMap = computed(() => {
		return map.value != undefined && (map.value.basemap == "alidade_smooth_dark" || map.value.basemap == "alidade_satellite" || satellite.value == true)
	});

	const currentLanguage = computed(() => {
		const langs = languages.value;

		const matchedLanguage = langs.find(l => l.lang == requestedLanguage.value)
		if (matchedLanguage) {
			return matchedLanguage.lang;
		} else if (langs.length > 0) {
			return langs[0].lang
		}
		return "sv" //shouldn't happen, languanges should always exist...
	});

	const currentLanguageSuffix = computed(() => {
		const lang = currentLanguage.value;
		const langs = languages.value;
		if (lang == langs[0]?.lang) {
			return "";
		}
		return `_${lang}`;
	});

	const languageSuffixes = computed(() => {
		return languages.value.map((lang: { lang: string }, index: number) => {
			return index === 0 ? "" : `_${lang.lang}`
		})
	})

	const languages = computed(() => {
		if (Array.isArray(map.value?.texts.language))
			return map.value.texts.language
		else
			return [{ lang: (map.value?.texts.language) || "sv" }]
	})

	function localized(target: any, prop: string, fallbackToDefault = true): string {

		//support target being a geojson feature...
		if (target.properties !== undefined) {
			target = target.properties;
		}
		const locProp = prop + currentLanguageSuffix.value;
		return target[locProp] || (fallbackToDefault ? target[prop] : "") || "";
	}

	const uiText = computed(() => {
		return loc[currentLanguage.value]
	})

	function setMap(m: MapData | undefined) {
		map.value = m;
	}

	function setSelectedRouteId(id: string) {
		selectedRouteId.value = id;
	}

	function setRoute(route: number[][][]) {
		if (map.value) {
			const r = map.value.routes.find((r) => r.id == selectedRouteId.value);
			if (r) {
				r.geometry.coordinates = route;
			} else {
				throw new Error(
					`Could not find route with id ${selectedRouteId.value}`
				);
			}
		}
	}

	function setRoutes(routes) {
		if (map.value) map.value.routes = routes;
	}

	function setMapState(state: MapState) {
		mapState.value = state;
	}

	function setLoadedSaveId(saveId: string) {
		loadedSaveId.value = saveId;
	}

	function setLatestSaveId(saveId: string) {
		latestSaveId.value = saveId;
	}

	function clearFreshnessState() {
		loadedSaveId.value = "";
		latestSaveId.value = "";
	}

	function setMarkers(markers: any) {
		if (map.value) {
			map.value.markers = markers;
		}
	}

	function setShapes(shapes: (Shape | ShapeLayer)[]) {
		if (map.value) {
			if (
				!map.value.shapeLayer ||
				!Array.isArray(map.value.shapeLayer.shapes)
			) {
				map.value.shapeLayer = { shapes: [] };
			}
			map.value.shapeLayer.shapes = shapes;
		}
	}

	function setRequestedLanguage(language: string | null) {
		requestedLanguage.value = language ?? "sv";
	}

	async function loadMap(mapId: string, draft: boolean) {
		setMapState(MapState.Loading);
		setMap(undefined);
		clearFreshnessState();
		const { success, data: mapData, error } = draft
			? await loadDraftMap(mapId)
			: await loadPublicMap(mapId, false);
		if (success) {
			const loadedMap = draft ? (mapData as DraftLoadData).map : mapData as MapData;
			setMap(loadedMap);
			if (draft) {
				const saveId = (mapData as DraftLoadData).saveId ?? "";
				setLoadedSaveId(saveId);
				setLatestSaveId(saveId);
			}
			document.title = loadedMap!.name;
			setSelectedRouteId(
				routes.value.length > 0 ? routes.value[0].id as string : ""
			);
			setMapState(MapState.Loaded);
			EventBus.emit("mapLoaded");
		} else {
			setMap(undefined);
			setMapState(
				error == "not_found" ? MapState.NotFound : MapState.Error
			);
		}
	}


	return {
		map,
		mapState,
		loadedSaveId,
		latestSaveId,
		selectedRouteId,
		requestedLanguage,
		satellite,
		videoMode,
		_editMode,
		blockUi,

		currentRoute,
		dropRoutes,
		drawFeature,
		trackFeature,
		trackIndex,
		trackFeatureLegs,
		trackFeatureRenderLegs,
		heightData,
		elevationGain,
		maxAltitude,
		hasElevation,
		trackLength,
		actualTrackLength,
		heightColorOverride,
		markerCategories,
		shapes,
		flatShapes,
		computedShapes,
		usedZoomStops,
		flatMarkers,
		computedCategories,
		computedMarkers,
		computedMarkersAll,
		computedMarkersNotGenerated,
		routes,
		isRoute,
		isShape,
		allFeatures,
		featuresBySlug,
		basemap,
		isDarkMap,
		currentLanguage,
		currentLanguageSuffix,
		languageSuffixes,
		languages,
		localized,
		uiText,
		isStale,

		mapInstance,

		setMap,
		setSelectedRouteId,
		setRoute,
		setRoutes,
		setMapState,
		categoryFilter,
		toggleCategoryFilter,
		clearCategoryFilter,
		setMarkers,
		setShapes,
		setRequestedLanguage,
		setLoadedSaveId,
		setLatestSaveId,
		clearFreshnessState,

		loadMap,
	}
})
